import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setGlobalGroupBy } from "store/features";
import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";
import { IGroupBy, IGroupByListColumn, IGroupByListItem } from "models/groupBy";

const GIVE_UP_TIMEOUT_MS = 30000;

export interface PendingAutoGroupBy {
  entityKind: EntityKind;
  columnName: string;
  groupName: string;
  // Units assigned to the column, if any. The refreshed category list may title a
  // unit-bearing column as "name (units)", so the match tries that form too.
  columnUnits?: string;
}

// Field mapping copied from components/filter/util/updateGroupBy.ts's converter defaults:
// property/groupByField = the matched column's property, empty default bin, Public pdenSource.
function buildGroupBy(column: IGroupByListColumn, entityKind: EntityKind): IGroupBy {
  return {
    property: column.property,
    groupByField: column.property,
    title: column.title,
    entityKind,
    canBin: column.canBin,
    dataType: column.dataType,
    categoryId: column.categoryId ?? 0,
    product: column.product,
    group: column.group,
    subgroup: column.subgroup,
    tooltip: "",
    display: "",
    pdenSource: "Public",
    bin: {
      BinSize: "",
      GreaterThan: "",
      LessThan: "",
      MinSize: "",
      MaxBins: "",
      MaxBinsSortOrder: "WellCount",
      BinType: "BinSize"
    }
  };
}

// Property-prefix markers that identify a custom (user/org/project) field, mirroring
// components/groupBy/hooks/useField. The imported column is always one of these.
const CUSTOM_FIELD_PROPERTY_MARKERS = [
  "My_Focus_Fields",
  "My_Midstream_Fields",
  "Org_Focus_Fields",
  "Org_Midstream_Fields",
  "Project_Focus_Fields"
];

function isCustomFieldColumn(column: IGroupByListColumn): boolean {
  const property = column.property ?? "";
  return CUSTOM_FIELD_PROPERTY_MARKERS.some((marker) => property.indexOf(marker) >= 0);
}

// IGroupByListColumn.title matches the bare name or the "name (units)" form. The imported
// column is always a custom field, so when a built-in field shares the same title (e.g. an
// existing "Gross XDA Constrained (ha)"), prefer the custom one under My/Org/Project Fields
// rather than grouping by the built-in. Within that, prefer an entry whose group === groupName.
function findMatchingColumn(
  list: IGroupByListItem[],
  columnName: string,
  groupName: string,
  columnUnits?: string
): Maybe<IGroupByListColumn> {
  const units = columnUnits?.trim();
  const acceptedTitles = units ? [columnName, `${columnName} (${units})`] : [columnName];
  const candidates: IGroupByListColumn[] = [];
  list.forEach((category) => {
    category.columns?.forEach((column) => {
      if (acceptedTitles.includes(column.title)) {
        candidates.push(column);
      }
    });
  });
  if (candidates.length === 0) return undefined;
  const customCandidates = candidates.filter(isCustomFieldColumn);
  const pool = customCandidates.length > 0 ? customCandidates : candidates;
  return pool.find((column) => column.group === groupName) ?? pool[0];
}

/**
 * Best-effort auto group-by after a successful import. Holds a hook-local pending target
 * and watches the category-list state that the post-save refresh repopulates; on the
 * first match it dispatches setGlobalGroupBy (which branches Well/Facility internally)
 * and clears the pending target. Give-up is silent: the first category-list refresh
 * without a match, or a 30s safety timeout, clears the pending target with no dispatch
 * and no error surfaced.
 */
export function useAutoGroupByAfterImport() {
  const dispatch = useDispatch();
  const [pending, setPending] = useState<Nullable<PendingAutoGroupBy>>(null);
  const baselineListRef = useRef<Nullable<IGroupByListItem[]>>(null);

  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const facilityFields = useSelector((state: RootState) => state.groupBy.facilityFields);

  const setPendingTarget = useCallback(
    (target: Nullable<PendingAutoGroupBy>) => {
      if (!target) {
        baselineListRef.current = null;
        setPending(null);
        return;
      }
      baselineListRef.current =
        target.entityKind === EntityKind.Well ? categoryList : facilityFields;
      setPending(target);
    },
    [categoryList, facilityFields]
  );

  useEffect(() => {
    if (!pending) return;

    const list = pending.entityKind === EntityKind.Well ? categoryList : facilityFields;
    // No refresh yet — the list is still the pre-import reference captured at set-time.
    if (list === baselineListRef.current) return;

    const match = findMatchingColumn(
      list,
      pending.columnName,
      pending.groupName,
      pending.columnUnits
    );
    if (match) {
      dispatch(setGlobalGroupBy(buildGroupBy(match, pending.entityKind)));
    }
    // Give up after this first refresh regardless of outcome — best-effort, one attempt.
    setPending(null);
  }, [pending, categoryList, facilityFields, dispatch]);

  useEffect(() => {
    if (!pending) return undefined;
    const timeoutId = setTimeout(() => setPending(null), GIVE_UP_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [pending]);

  return { setPendingTarget };
}
